import {type MapBoundsLocation, useLocationsRequest} from '@yandex-delivery/blocks'
import type {Point} from '@yandex-delivery/core/types'
import {isPoint} from '@yandex-taxi/react-integration-api'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {config} from 'src/shared/config'

import {MapModes} from '../../../types'
import {isPerformerImageLoadable} from '../../../utils'
import {useMargin} from '../../margin-provider'
import type {BaseUIProps, Props} from '../model'

type MapBoundsLocationRequest = MapBoundsLocation & {
  duration: number
}

type MapBoundsLocationRequestByMode = MapBoundsLocationRequest & {
  mode: MapModes
}

function checkIsPlacemarkVisible(mode: MapModes) {
  return [MapModes.WaitCourier, MapModes.SearchCourier].includes(mode)
}

function checkIsRouteVisible(mode: MapModes) {
  return [MapModes.SearchCourier, MapModes.WaitCourier, MapModes.Transporting].includes(mode)
}

function getMapBoundsLocation(route: Point[]): MapBoundsLocationRequest {
  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity

  route.forEach(routePoint => {
    const [longitude, latitude] = routePoint

    if (longitude < minLng) minLng = longitude
    if (latitude < minLat) minLat = latitude
    if (longitude > maxLng) maxLng = longitude
    if (latitude > maxLat) maxLat = latitude
  })

  return {
    bounds: [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
    duration: 300,
  }
}

const useBoundsByMode = (mode: MapModes, bounds?: MapBoundsLocationRequest) => {
  const [boundsByMode, setBoundsByMode] = useState<MapBoundsLocationRequestByMode>()
  const staleBoundsRef = useRef(bounds)

  // На момент смены mode в bounds лежит результат ещё прошлого этапа маршрута
  // (например, узкий бокс у точки А после WaitCourier) — захват таких bounds
  // отстреливал камеру (CARGODEV-85954). Помечаем их протухшими и ждём первых
  // bounds, посчитанных уже для маршрута нового режима.
  // biome-ignore lint/correctness/useExhaustiveDependencies: снимок bounds делаем именно на смену mode
  useEffect(() => {
    staleBoundsRef.current = bounds
  }, [mode])

  // Захватываем bounds один раз на mode; до их прихода камера остаётся
  // на предыдущем кадре
  useEffect(() => {
    if (bounds && bounds !== staleBoundsRef.current) {
      setBoundsByMode(prev =>
        prev?.mode === mode
          ? prev
          : {
              ...bounds,
              mode,
            },
      )
    }
  }, [mode, bounds])

  return boundsByMode
}

const DEFAULT_ROUTE = [] as const

export function useController(props: Props): BaseUIProps {
  const {mode, route = DEFAULT_ROUTE, bubbleText, isRouteVisible, position, pin, direction, theme} = props
  const margin = useMargin()

  const routePoints = useMemo(() => {
    return route.map(({point}) => point).filter(isPoint)
  }, [route])

  const [bounds, setBounds] = useState<MapBoundsLocationRequest>()
  const boundsByMode = useBoundsByMode(mode, bounds)

  const performer = useMemo(() => {
    if (!position || !pin) return undefined
    if (isPerformerImageLoadable(pin)) return {direction, point: position, tag: pin.image_tag, anchor: pin.anchor}
    return {direction, point: position}
  }, [direction, pin, position])

  const placemarkCoordinatesByMode = useMemo(() => {
    // Для режимов WaitCourier, SearchCourier и Transporting показываем последнюю точку (destination)
    if (checkIsPlacemarkVisible(mode) || mode === MapModes.Transporting) {
      return routePoints[routePoints.length - 1]
    }

    // Для Delivered тоже показываем последнюю точку (место доставки)
    if (mode === MapModes.Delivered && routePoints.length > 0) {
      return routePoints[routePoints.length - 1]
    }

    // Если нет точек маршрута - используем координаты пользователя
    if (config.userCoordinates) {
      return [config.userCoordinates.longitude, config.userCoordinates.latitude] as Point
    }

    return undefined // fallback на DEFAULT_CENTER
  }, [mode, routePoints])

  const waypointsCoordinates = useMemo(() => {
    return routePoints.slice(0, -1)
  }, [routePoints])

  const routeCoordinatesByMode = useMemo(() => {
    if (!isRouteVisible || !checkIsRouteVisible(mode)) {
      return []
    }

    // Если есть live позиция курьера - рисуем от неё (обычная шарилка)
    if (performer) {
      return [performer.point, ...routePoints]
    }

    // Если нет live позиции, но есть точки маршрута - рисуем между ними (auth шарилка)
    return routePoints.length > 1 ? routePoints : []
  }, [isRouteVisible, mode, performer, routePoints])

  // Поллинг state пересоздаёт массив координат с теми же значениями, а новая
  // ссылка location у YMap заново запускает перелёт камеры (CARGODEV-85954) —
  // поэтому мемоизируем центр по примитивам, а не по ссылке на массив
  const [placemarkLng, placemarkLat] = placemarkCoordinatesByMode ?? []
  const centerCoords = useMemo<Point | undefined>(
    () => (placemarkLng != null && placemarkLat != null ? [placemarkLng, placemarkLat] : undefined),
    [placemarkLng, placemarkLat],
  )

  const mapLocation = useLocationsRequest({
    ...boundsByMode,
    centerCoords,
  })

  const handleLoadBoundsError = useCallback(() => {
    if (routeCoordinatesByMode.length > 0) {
      const mapBoundsLocation = getMapBoundsLocation(routeCoordinatesByMode)
      setBounds(mapBoundsLocation)
    }
  }, [routeCoordinatesByMode])

  return {
    bubbleText,
    mapLocation,
    margin,
    performer,
    placemarkCoordinates: placemarkCoordinatesByMode,
    routeCoordinates: routeCoordinatesByMode,
    onLoadBoundsSuccess: setBounds,
    onLoadBoundsError: handleLoadBoundsError,
    waypointsCoordinates,
    theme,
  }
}
